import type { FocusedField } from '../types';

/**
 * Valid if it parses as a URL. Supports:
 * - http:// and https://
 * - www.example.com (prepends https:// to parse)
 * - bare domain like change.org (prepends https:// only when it contains a dot)
 */
export const isValidUrl = (url: string | undefined): boolean => {
	const trimmedUrl = typeof url === 'string' ? url.trim() : '';
	if (!trimmedUrl) return false;
	try {
		const hasProtocol = trimmedUrl.startsWith('http://') || trimmedUrl.startsWith('https://');
		const looksLikeDomain = trimmedUrl.startsWith('www.') || trimmedUrl.includes('.');
		let toParse = trimmedUrl;
		if (!hasProtocol && looksLikeDomain) toParse = `https://${trimmedUrl}`;
		// eslint-disable-next-line no-new
		new URL(toParse);
		return true;
	} catch {
		return false;
	}
};

/**
 * Returns a URL safe for href. Prepends https:// when there is no protocol.
 */
export const normalizeUrlForHref = (url: string | undefined): string => {
	const s = url?.trim() ?? '';
	if (!s) return s;
	if (s.startsWith('http://') || s.startsWith('https://')) return s;
	return `https://${s}`;
};

/**
 * Checks if a specific field is currently focused
 * @param currentField - The field to check
 * @param focusedField - The currently focused field
 * @returns true if the field is focused, false otherwise
 */
export const isFieldFocused = (currentField: FocusedField, focusedField: FocusedField): boolean => {
	return focusedField === currentField;
};

/**
 * Determines if text should be highlighted based on focus and content state
 * @param isFocused - Whether the field is focused
 * @param hasContent - Whether the field has actual content (not placeholder)
 * @returns true if text should be highlighted, false otherwise
 */
export const shouldHighlightText = (isFocused: boolean, hasContent: boolean): boolean => {
	return isFocused && hasContent;
};
